import { useEffect, useState } from "react";
import { Loader2, DollarSign, TrendingUp, Users, AlertCircle, Activity } from "lucide-react";

import { api } from "@/lib/auth-context";

function Card({ label, value, sub, icon: Icon, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-xl p-6"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
          {label}
        </p>
        <span
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: accent || "#0A192F" }}
        >
          <Icon className="w-4 h-4 text-[#C5A059]" />
        </span>
      </div>
      <p className="mt-4 font-heading text-3xl font-bold text-[#0A192F]">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function fmtUsd(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

export default function AdminBilling() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [m, r, t, a] = await Promise.all([
          api.get("/admin/billing/metrics"),
          api.get("/admin/billing/revenue-by-month?months=12"),
          api.get("/admin/billing/recent-payments?limit=20"),
          api.get("/admin/ai/usage?days=30"),
        ]);
        if (!alive) return;
        setMetrics(m.data);
        setRevenue(r.data?.data || []);
        setTransactions(t.data?.transactions || []);
        setAiUsage(a.data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
      </div>
    );
  }

  const maxRevenue = Math.max(1, ...revenue.map((r) => r.revenue_usd));

  return (
    <div data-testid="admin-billing" className="p-6 lg:p-10 max-w-7xl">
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
        Revenue
      </p>
      <h1 className="font-heading text-3xl font-bold text-[#0A192F] mb-8">
        Billing & SaaS metrics
      </h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Card
          label="MRR"
          value={fmtUsd(metrics?.mrr_usd)}
          sub="Monthly Recurring"
          icon={DollarSign}
          testId="billing-mrr"
        />
        <Card
          label="ARR"
          value={fmtUsd(metrics?.arr_usd)}
          sub="Annual run-rate"
          icon={TrendingUp}
          testId="billing-arr"
        />
        <Card
          label="Active subs"
          value={metrics?.active_subscriptions ?? 0}
          sub={`${metrics?.trial_users ?? 0} trialing`}
          icon={Users}
          testId="billing-active-subs"
        />
        <Card
          label="Churn (30d)"
          value={`${metrics?.churn_rate_pct_30d ?? 0}%`}
          sub={`${metrics?.canceled_last_30d ?? 0} canceled`}
          icon={AlertCircle}
          accent={(metrics?.churn_rate_pct_30d ?? 0) > 5 ? "#7F1D1D" : "#0A192F"}
          testId="billing-churn"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-slate-200 rounded-xl p-6 lg:col-span-2">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
            Revenue by month
          </p>
          {revenue.length === 0 ? (
            <p className="text-sm text-slate-500">No paid transactions yet.</p>
          ) : (
            <div className="flex items-end gap-3 h-44" data-testid="billing-revenue-chart">
              {revenue.map((row) => {
                const pct = Math.max(2, (row.revenue_usd / maxRevenue) * 100);
                return (
                  <div key={row.month} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-[#0A192F] rounded-t-md hover:bg-[#C5A059] transition"
                      style={{ height: `${pct}%` }}
                      title={`${row.month}: ${fmtUsd(row.revenue_usd)}`}
                    />
                    <p className="text-[10px] text-slate-500 mt-2">{row.month.slice(5)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-3">
            Status breakdown
          </p>
          <ul className="space-y-2 text-sm">
            {Object.entries(metrics?.counts_by_status || {}).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between text-slate-700">
                <span className="capitalize">{k.replace("_", " ")}</span>
                <span className="font-semibold text-[#0A192F]">{v}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-200 mt-4 pt-4">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-2">
              Past due
            </p>
            <p className="font-heading text-2xl text-[#0A192F]" data-testid="billing-past-due">
              {metrics?.past_due ?? 0}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-10">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
            Recent payments
          </p>
          <span className="text-xs text-slate-500">{transactions.length} shown</span>
        </div>
        {transactions.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">No transactions yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">When</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">User</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Plan</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Amount</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id || t.stripe_session_id} className="border-t border-slate-100">
                  <td className="px-6 py-3 text-slate-700">
                    {(t.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-6 py-3 text-slate-700">
                    {(t.user_id || "").slice(0, 12)}…
                  </td>
                  <td className="px-6 py-3 text-slate-700 capitalize">{t.plan || "—"}</td>
                  <td className="px-6 py-3 text-slate-700">
                    {fmtUsd(((t.amount_cents || 0) / 100))}
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        t.status === "paid"
                          ? "bg-emerald-50 text-emerald-700"
                          : t.status === "initiated"
                          ? "bg-amber-50 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
            AI usage — last 30 days
          </p>
          <Activity className="w-4 h-4 text-[#C5A059]" />
        </div>
        {!aiUsage || (aiUsage.totals?.calls ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">No AI calls recorded yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div>
                <p className="text-xs uppercase text-slate-500">Total calls</p>
                <p className="font-heading text-xl text-[#0A192F]">{aiUsage.totals.calls}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Tokens</p>
                <p className="font-heading text-xl text-[#0A192F]">
                  {(aiUsage.totals.total_tokens || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Est. cost</p>
                <p className="font-heading text-xl text-[#0A192F]">
                  ${(aiUsage.totals.cost_usd || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Failures</p>
                <p className="font-heading text-xl text-[#0A192F]">{aiUsage.totals.failures}</p>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-500">
                  <th className="text-left py-2">Task</th>
                  <th className="text-right py-2">Calls</th>
                  <th className="text-right py-2">Tokens</th>
                  <th className="text-right py-2">Cost</th>
                  <th className="text-right py-2">Avg latency</th>
                </tr>
              </thead>
              <tbody>
                {aiUsage.by_task.map((t) => (
                  <tr key={t.task} className="border-t border-slate-100">
                    <td className="py-2 text-slate-700">{t.task}</td>
                    <td className="py-2 text-right text-slate-700">{t.calls}</td>
                    <td className="py-2 text-right text-slate-700">
                      {(t.total_tokens || 0).toLocaleString()}
                    </td>
                    <td className="py-2 text-right text-slate-700">${(t.cost_usd || 0).toFixed(2)}</td>
                    <td className="py-2 text-right text-slate-700">{t.avg_latency_ms} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
